import { useEffect, useRef, useState } from 'react';
import { callApi } from 'src/utils/apiCall';
import { OUTCOME } from './callLog.constants';
import { detailUrl } from './callLogQuery';

// Our own transcript + AI summary for one Elevate desk-phone call (CU-869fkj6eg).
// The lead page drives the same endpoint from a button
// (leadDetail/deskCalls/leadDeskCalls.api.js); the review window runs it when the
// call is opened instead, so nobody meets an empty panel with no way forward.
// The endpoint is idempotent and never pays twice, so reopening the call, a
// double click or a retry all cost nothing.
export const generateSummaryUrl = (practiceId, callId) =>
  `${detailUrl(practiceId, callId)}generate-summary/`;

const POLL_MS = 4000;
// Transcription takes ~10s and the summary a few more. Stop claiming progress
// after ~80s instead of spinning for ever; the text still lands on its own and
// shows on the next open.
const POLL_LIMIT = 20;

// Only where the backend would accept it (contract B7 §2) and nothing is already
// on its way. `not_available` is the server's own verdict: `waiting_for_elevate`
// means Elevate's free summary may still arrive, and we never pay over the top
// of one. `queued` means the work already exists.
export const canGenerateFor = (call) =>
  Boolean(call)
  && call.provider === 'elevate'
  && call.channel !== 'whatsapp'
  && call.outcome === OUTCOME.ANSWERED
  && call.hasRecording === true
  && call.summaryStatus === 'not_available';

const hasContent = (call) => Boolean(call?.transcript) || Boolean(call?.aiReview);

export default function useDeskCallAi({ practiceId, call, onRefresh }) {
  const id = call?.id || null;
  const [state, setState] = useState({ id: null, phase: 'idle' });
  const requested = useRef(new Set());
  const polls = useRef(0);
  // useCallDetail rebuilds `retry` on every render; a ref keeps the polling
  // effect from restarting on each one.
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  useEffect(() => {
    if (!practiceId || !id) return undefined;
    if (requested.current.has(id) || hasContent(call) || !canGenerateFor(call)) return undefined;
    requested.current.add(id);
    let live = true;
    polls.current = 0;
    setState({ id, phase: 'starting' });
    callApi(generateSummaryUrl(practiceId, id), 'POST', {}, false, { maxRetries: 0 }).then((response) => {
      if (!live) return;
      // Anyone who can open the call can ask for this. A 400/409 (not eligible)
      // or a 403 from an older backend is not an error to shout about: the
      // panel falls back to the server's own status wording.
      if (response?.ok) setState({ id, phase: 'working' });
      else setState({ id, phase: response?.status === 403 ? 'denied' : 'unavailable' });
    });
    return () => {
      live = false;
    };
  }, [practiceId, id, call]);

  useEffect(() => {
    if (state.id !== id || state.phase !== 'working') return undefined;
    if (hasContent(call)) {
      setState({ id, phase: 'done' });
      return undefined;
    }
    if (polls.current >= POLL_LIMIT) {
      setState({ id, phase: 'slow' });
      return undefined;
    }
    const timer = setTimeout(() => {
      polls.current += 1;
      refresh.current?.();
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [state.id, state.phase, id, call]);

  const phase = state.id === id ? state.phase : 'idle';
  return { phase, busy: phase === 'starting' || phase === 'working', slow: phase === 'slow' };
}
